import { useCallback, useEffect, useState } from 'react';

import { calculateTimeRemaining, formatCountdownTime } from '@/utils/timeFormatHelpers';

interface UseCountdownOptions {
  expiresAt?: number; // Unix timestamp
  onExpire?: () => void;
  defaultDuration?: number; // Default duration in seconds for progress calculation
}

interface UseCountdownReturn {
  timeLeft: number;
  isExpired: boolean;
  progressPercentage: number;
  formatTime: (seconds: number) => string;
}

/**
 * React hook for managing a countdown timer based on an optional expiration timestamp.
 *
 * Tracks the remaining time in seconds, expiration status, and progress percentage relative to a configurable default duration. Invokes an optional callback when the countdown expires and provides a function to format the remaining time for display.
 *
 * @param expiresAt - Optional Unix timestamp (in seconds) indicating when the countdown should expire.
 * @param onExpire - Optional callback invoked once when the countdown reaches zero.
 * @param defaultDuration - Optional duration in seconds used as the base for progress percentage calculation.
 * @returns An object containing the current time left, expiration status, progress percentage, and a formatting function.
 *
 * @remark If {@link expiresAt} is not a positive number, the countdown is considered inactive and progress will be zero.
 */
export function useCountdown({
  expiresAt,
  onExpire,
  defaultDuration
}: UseCountdownOptions): UseCountdownReturn {
  // Validate expiresAt input
  const validExpiresAt = expiresAt && expiresAt > 0 ? expiresAt : undefined;

  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);
  const [initialDuration, setInitialDuration] = useState<number | null>(null);

  const calculateTimeLeft = useCallback(
    () => (validExpiresAt ? calculateTimeRemaining(validExpiresAt) : 0),
    [validExpiresAt]
  );

  const formatTime = useCallback((seconds: number): string => formatCountdownTime(seconds), []);

  const getProgressPercentage = useCallback((): number => {
    if (!expiresAt) return 0;

    // Always use defaultDuration (5 minutes) as the base for progress calculation
    if (defaultDuration === null || defaultDuration === undefined || defaultDuration === 0)
      return 0;

    // Calculate remaining time percentage (starts at 100% and goes down to 0%)
    const progress = Math.min(100, Math.max(0, (timeLeft / defaultDuration) * 100));
    return progress;
  }, [expiresAt, defaultDuration, timeLeft]);

  useEffect(() => {
    const updateTimeLeft = () => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      const expired = remaining === 0 && expiresAt !== undefined;
      setIsExpired(expired);

      // Set initial duration on first calculation when we have a valid remaining time
      if (initialDuration === null && remaining > 0) {
        setInitialDuration(remaining);
      }

      // Call onExpire callback when expired
      if (expired && onExpire) {
        onExpire();
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeLeft, initialDuration, onExpire, expiresAt]);

  // Reset initial duration when expiresAt changes (e.g., after refresh)
  useEffect(() => {
    setInitialDuration(null);
    // Don't reset isExpired here - let the main effect handle expiration state
  }, [expiresAt]);

  return {
    timeLeft,
    isExpired,
    progressPercentage: getProgressPercentage(),
    formatTime
  };
}
